import { useInjectReducer } from "@app/store";
import Button from "@components/Button";
import Checkbox from "@components/Checkbox";
import InputWrapper from "@components/Input";
import PageContent from "@components/PageContent";
import PageHeader from "@components/PageHeader";
import SelectWrapper from "@components/Select";
import {
  useLoadOneRoleDetailsMutation,
  useSaveRoleMutation,
} from "@features/admin/roles/rolesApiSlice";
import { makeSelectOne } from "@features/admin/roles/rolesSelector";
import rolesSlice, {
  clearOne,
  setOneValue,
} from "@features/admin/roles/rolesSlice";
import { enqueueSnackbar } from "@features/auth/authSlice";
import { APP_NAME } from "@utils/constants";
import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

const RolesAddEdit = () => {
  useInjectReducer("roles", rolesSlice);
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loadOne, { isLoading: loading }] = useLoadOneRoleDetailsMutation();

  const [saveOne, { isLoading: saveLoading }] = useSaveRoleMutation();
  const one = useSelector(makeSelectOne);

  useEffect(() => {
    if (id) {
      loadOne(id);
    } else {
      dispatch(clearOne());
    }
  }, [id]);

  const handleChange = (name) => (e) => {
    dispatch(setOneValue({ key: name, value: e.target.value }));
  };

  const handleChangeDropdown = (name) => (e) => {
    dispatch(setOneValue({ key: name, value: e.value }));
  };

  const handleChangeChecked = (name) => (e) => {
    dispatch(setOneValue({ key: name, value: e.target.checked }));
  };

  const handleSaveRole = () => {
    saveOne(one)
      .unwrap()
      .then((res) => {
        if (res?.success) {
          dispatch(
            enqueueSnackbar({
              message: res?.msg ?? "Page Saved.",
              options: { variant: "success" },
            })
          );
          navigate("/admin/role-manage");
        }
      })
      .catch((err) => {
        dispatch(
          enqueueSnackbar({
            message: err?.data?.msg ?? "Page Save Failed.",
            options: {
              variant: "warning",
            },
          })
        );
      });
  };
  return (
    <>
      <Helmet>
        <title>
          {id ? `Edit Role | ${APP_NAME}` : `Add Role | ${APP_NAME}`}
        </title>
      </Helmet>
      <PageHeader
        isBack
        handleGoBack={() => navigate("/admin/role-manage")}
        title={!id ? "Add Role" : "Edit Role"}
      />
      <PageContent loading={loading}>
        <div className="bg-white p-4 border border-gray-300">
          <InputWrapper
            label="Role Title"
            id="role-title"
            name="role-title"
            type="text"
            value={one?.role_title ?? ""}
            onChange={handleChange("role_title")}
          />

          <InputWrapper
            label="Description"
            id="description"
            name="description"
            type="text"
            value={one?.description ?? ""}
            onChange={handleChange("description")}
          />

          <SelectWrapper
            options={roleTypOptions}
            value={roleTypeOptionsNormalized[one?.role_type] ?? ""}
            onChange={handleChangeDropdown("role_type")}
            label="Select Type"
          />
          <div className="flex items-center gap-2">
            <Checkbox
              label="Is Active"
              checked={one?.is_active ?? false}
              name="is_active"
              onChange={handleChangeChecked("is_active")}
            />

            <Checkbox
              label="View For Seller"
              checked={one?.is_seller ?? false}
              name="is_seller"
              onChange={handleChangeChecked("is_seller")}
            />
          </div>
          <Button
            className="mt-4"
            loading={saveLoading}
            onClick={handleSaveRole}
          >
            Save
          </Button>
        </div>
      </PageContent>
    </>
  );
};

export default RolesAddEdit;

const roleTypOptions = [
  { label: "Admin", value: "admin" },
  { label: "Seller", value: "seller" },
  { label: "User", value: "user" },
  { label: "Developer", value: "developer" },
  { label: "Others", value: "others" },
];

const roleTypeOptionsNormalized = roleTypOptions.reduce(
  (acc, curr) => ({ ...acc, [curr.value]: curr }),
  {}
);
